// ─────────────────────────────────────────────────────────────
// src/BulkImport.js — Bulk product import for SuqPOS
// Supports: Excel (.xlsx), CSV, and pre-loaded Ethiopian catalog
// ─────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import { DEFAULT_CATALOG_IMPORT_ENABLED } from "./featureFlags";
import { useMaximize } from "./hooks/useMaximize";

// ── Pre-loaded Ethiopian mini market catalog ──────────────────
const ETHIOPIAN_CATALOG = [
  {
    name: "Doka Professional Shampoo Vitamin E + Castor Oil 500ml",
    category: "Personal Care",
    description:
      "Professional hair nourishing shampoo enriched with Vitamin E and Castor Oil",
    shelf_location: "",
  },
  {
    name: "Doka Professional Conditioner Vitamin E + Castor Oil 500ml",
    category: "Personal Care",
    description:
      "Professional hair nourishing conditioner enriched with Vitamin E and Castor Oil.",
    shelf_location: "",
  },
  {
    name: "Black Root Heavy Paraffin Oil Hair & Skin 500ml",
    category: "Personal Care",
    description: "Heavy paraffin oil formula for hair and skin care treatment.",
    shelf_location: "",
  },
  {
    name: "Sunsilk Nourishing Shampoo Avocado Green",
    category: "Personal Care",
    description: "Nourishing hair conditioner for smooth and manageable hair.",
    shelf_location: "",
  },
  {
    name: "Taflen Heavy Paraffin Oil Hair Food",
    category: "Personal Care",
    description: "Heavy paraffin oil hair food and scalp treatment.",
    shelf_location: "",
  },
  {
    name: "Bio Galaxy Brazilian Keratin Shampoo Natural Extract",
    category: "Personal Care",
    description:
      "Brazilian keratin shampoo with natural extracts for hair strength.",
    shelf_location: "",
  },
  {
    name: "TRESemmé Moisture Rich Shampoo Luxurious Moisture",
    category: "Personal Care",
    description:
      "Luxurious moisture shampoo for dry or damaged hair salon quality.",
    shelf_location: "",
  },
  {
    name: "Coca-Cola 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Classic Coca-Cola sparkling soft drink in a 500ml PET plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Coca-Cola 300ml Glass Bottle",
    category: "Beverages",
    description: "Classic Coca-Cola soft drink in a 300ml glass bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Fanta Orange 300ml Glass Bottle",
    category: "Beverages",
    description:
      "Orange flavored sparkling soft drink in a returnable 300ml glass bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Fanta Orange 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Orange flavored sparkling soft drink in a 500ml PET plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Fanta Pineapple 300ml Glass Bottle",
    category: "Beverages",
    description:
      "Pineapple flavored sparkling soft drink in a returnable 300ml glass bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Fanta Pineapple 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Pineapple flavored sparkling soft drink in a 500ml PET plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Sprite Lemon-Lime 300ml Glass Bottle",
    category: "Beverages",
    description:
      "Crisp lemon-lime flavored sparkling soft drink in a returnable 300ml glass bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Sprite Lemon-Lime 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Crisp lemon-lime flavored sparkling soft drink in a 500ml PET plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Ambo Mineral Water 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Ambo sparkling natural mineral water in a 500ml plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Predator Energy Drink Gold 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Predator carbonated energy drink with taurine in a 500ml plastic bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "Schweppes Novida 300ml Glass Bottle",
    category: "Beverages",
    description:
      "Schweppes Novida pineapple-flavored sparkling soft drink in a returnable 300ml glass bottle.",
    shelf_location: "Beverage Cooler",
  },
  {
    name: "V-Bull Energy Drink 500ml Plastic Bottle",
    category: "Beverages",
    description: "V-Bull energy drink in a 500ml PET plastic bottle.",
    shelf_location: "Dairy & Drink Cooler",
  },
  {
    name: "Pepsi Cola 500ml Plastic Bottle",
    category: "Beverages",
    description:
      "Classic Pepsi carbonated soft drink in a 500ml PET plastic bottle.",
    shelf_location: "Dairy & Drink Cooler",
  },
  {
    name: "Aquadis Purified Still Water 500ml",
    category: "Beverages",
    description:
      "Standard 500ml plastic bottle of purified still drinking water.",
    shelf_location: "Dairy & Drink Cooler",
  },
  {
    name: "St. George Beer 330ml Glass Bottle",
    category: "Beverages",
    description:
      "Classic St. George lager beer in a 330ml returnable amber glass bottle.",
    shelf_location: "Dairy & Drink Cooler",
  },
  {
    name: "Rani Float Mango Fruit Drink",
    category: "Beverages",
    description:
      "Rani brand mango fruit drink featuring the classic orange and blue logo.",
    shelf_location: "",
  },
  {
    name: "KK Mango Fruit Drink Large",
    category: "Beverages",
    description:
      "KK brand mango fruit drink in a large translucent plastic bottle with a green cap.",
    shelf_location: "",
  },
  {
    name: "KK Mango Fruit Drink Small",
    category: "Beverages",
    description: "KK brand mango fruit drink in a small plastic bottle.",
    shelf_location: "",
  },
  {
    name: "Rosanna Mezze Maniche Pasta 500g",
    category: "Dry Goods & Grains",
    description: "Premium Rosanna Mezze Maniche shape pasta pack.",
    shelf_location: "",
  },
  {
    name: "Rosanna Margherita Pasta 500g",
    category: "Dry Goods & Grains",
    description: "Premium Rosanna Margherita shape pasta pack.",
    shelf_location: "",
  },
  {
    name: "Prima Long Spaghetti Pasta 500g",
    category: "Dry Goods & Grains",
    description: "Prima brand long spaghetti pasta.",
    shelf_location: "",
  },
  {
    name: "Mawel Long Pasta Spaghetti 500g",
    category: "Dry Goods & Grains",
    description: "Long straight pasta spaghetti pack.",
    shelf_location: "",
  },
  {
    name: "Mama Mia MFC Spaghetti Long Pasta 500g",
    category: "Dry Goods & Grains",
    description: "Mama Mia MFC brand spaghetti long pasta.",
    shelf_location: "",
  },
  {
    name: "OK Long Spaghetti Pasta 500g",
    category: "Dry Goods & Grains",
    description: "OK brand long spaghetti pasta.",
    shelf_location: "",
  },
  {
    name: "Normal Macaroni (መኮሮኒ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Bulk elbow macaroni pasta sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Penne Macaroni (መኮሮኒ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Bulk penne shape pasta sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Fortune Biryani Special Basmati Rice 1kg",
    category: "Dry Goods & Grains",
    description:
      "Fortune brand premium long-grain Basmati rice, ideal for biryani.",
    shelf_location: "",
  },
  {
    name: "Indian Choice Premium Golden Sella Basmati Rice 5kg",
    category: "Dry Goods & Grains",
    description:
      "Premium long-grain Golden Sella Basmati rice bag with handle.",
    shelf_location: "",
  },
  {
    name: "Rice (ሩዝ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Bulk rice sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Yene Fortified Corn Flour 1kg",
    category: "Dry Goods & Grains",
    description:
      "Yene brand premium quality fortified corn flour in a 1kg bag.",
    shelf_location: "",
  },
  {
    name: "Scandinavian Fibre Oats 500g",
    category: "Confectionery & Snacks",
    description:
      "Happy Heart Scandinavian Fibre Oats oat flakes in a 500g stand-up pouch.",
    shelf_location: "",
  },
  {
    name: "Quaker Quick Cooking White Oats Can",
    category: "Confectionery & Snacks",
    description:
      "Quaker brand quick cooking white oats in a classic yellow and dark blue metal tin.",
    shelf_location: "",
  },
  {
    name: "Red Split Lentils (የተከካ ምስር) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Split red lentils sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Split Yellow Peas (ክክ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Split yellow peas sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Raw Green Coffee Beans (ቡን) - Bulk per kg",
    category: "Coffee & Tea",
    description: "Raw unroasted green coffee beans sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Faba Beans (ባቄላ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Whole dried faba beans sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Red Kidney Beans (ቦሎቄ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Dried red kidney beans sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Chickpeas (ሽምብራ) - Bulk per kg",
    category: "Dry Goods & Grains",
    description: "Whole dried chickpeas sold loose by weight.",
    shelf_location: "Bulk Floor Bins",
  },
  {
    name: "Nestle Coffee-Mate Coffee Creamer 400g",
    category: "Coffee & Tea",
    description:
      "Nestlé Coffee-Mate original flavor coffee creamer in a 400g plastic jar.",
    shelf_location: "",
  },
  {
    name: "Frozen Chicken Pack",
    category: "Frozen & Chilled",
    description:
      "Pre-packed frozen chicken parts in plastic-wrapped styrofoam trays.",
    shelf_location: "",
  },
  {
    name: "Frozen Fish Fillet Pack",
    category: "Frozen & Chilled",
    description: "Pre-packed frozen fish fillets in clear plastic packaging.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Tomato Flavor Big",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in green packaging, Tomato flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Tomato Flavor Medium",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in green packaging, Tomato flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Tomato Flavor Small",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in green packaging, Tomato flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Habesha Spice Flavor Big",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in red packaging, Habesha Spice flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Habesha Spice Flavor Medium",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in red packaging, Habesha Spice flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Habesha Spice Flavor Small",
    category: "Confectionery & Snacks",
    description:
      "Sun Chips local potato chips in red packaging, Habesha Spice flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Natural Salted Flavor Small",
    category: "Confectionery & Snacks",
    description: "Sun Chips local potato chips, Natural Salted flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Natural Salted Flavor Medium",
    category: "Confectionery & Snacks",
    description: "Sun Chips local potato chips, Natural Salted flavor.",
    shelf_location: "",
  },
  {
    name: "Sun Chips Natural Salted Flavor Big",
    category: "Confectionery & Snacks",
    description: "Sun Chips local potato chips, Natural Salted flavor.",
    shelf_location: "",
  },
  {
    name: "Safa Tomato Paste Canned",
    category: "Canned & Packaged Foods",
    description: "Safa brand premium tomato paste in a red metal can.",
    shelf_location: "",
  },
  {
    name: "Aroma Foul Medames",
    category: "Canned & Packaged Foods",
    description:
      "Aroma brand extra grade Foul Medames (fava beans) in a yellow metal can.",
    shelf_location: "",
  },
  {
    name: "Kent Vanillin with Sugar 10 Pack",
    category: "Canned & Packaged Foods",
    description:
      "Kent brand vanillin sugar powder economic pack, containing 10 individual sachets.",
    shelf_location: "",
  },
  {
    name: "Chapa Mandashi Baking Powder Large Box",
    category: "Canned & Packaged Foods",
    description: "Chapa Mandashi brand baking powder in a large yellow box.",
    shelf_location: "",
  },
  {
    name: "Chapa Mandashi Baking Powder Small Box",
    category: "Canned & Packaged Foods",
    description:
      "Chapa Mandashi brand baking powder in a small blue rectangular box.",
    shelf_location: "",
  },
  {
    name: "Dachi White Vinegar Plastic Bottle",
    category: "Oil & Condiments",
    description:
      "Dachi brand clear white vinegar in a plastic bottle with a green cap.",
    shelf_location: "",
  },
  {
    name: "Com Puro Vinegar Plastic Bottle",
    category: "Oil & Condiments",
    description:
      "Puro Vinegar in a plastic bottle with an orange cap and label.",
    shelf_location: "",
  },
  {
    name: "Patchee Chocolate Spread Tub",
    category: "Confectionery & Snacks",
    description:
      "Patchee brand chocolate spread in a clear plastic tub with an orange lid.",
    shelf_location: "",
  },
  {
    name: "Dachi Ketchup Large Red Jug",
    category: "Oil & Condiments",
    description:
      "Dachi brand tomato ketchup in a large red plastic commercial jug with a handle.",
    shelf_location: "",
  },
  {
    name: "Consul Pomace Olive Oil Large Bottle",
    category: "Oil & Condiments",
    description:
      "Consul brand refined pomace olive oil blended with extra virgin olive oil, large dark green glass bottle.",
    shelf_location: "",
  },
  {
    name: "Consul Pomace Olive Oil Small Bottle",
    category: "Oil & Condiments",
    description:
      "Consul brand refined pomace olive oil blended with extra virgin olive oil, small dark green glass bottle.",
    shelf_location: "",
  },
  {
    name: "Nib Chocolate Spread Large Tub",
    category: "Confectionery & Snacks",
    description:
      "Nib brand chocolate spread in a bright red plastic commercial tub.",
    shelf_location: "",
  },
  {
    name: "Theday Fruit Jam Strawberry 430g",
    category: "Confectionery & Snacks",
    description:
      "Theday brand premium fruit jam, strawberry flavor, in a glass jar.",
    shelf_location: "",
  },
  {
    name: "Miami Mix Fruit Jam",
    category: "Confectionery & Snacks",
    description:
      "Miami brand mixed fruit jam in a tall glass jar with a white lid.",
    shelf_location: "",
  },
  {
    name: "Premium Kitchen Tomato Ketchup Bottle",
    category: "Oil & Condiments",
    description:
      "Tomato ketchup in a clear glass bottle with a white neck label.",
    shelf_location: "",
  },
  {
    name: "Kent Chocolate Syrup Bottle",
    category: "Confectionery & Snacks",
    description:
      "Kent brand chocolate flavored syrup in a squeezing orange plastic bottle.",
    shelf_location: "",
  },
  {
    name: "Gold Valley Original BBQ Sauce",
    category: "Oil & Condiments",
    description:
      "Gold Valley brand original barbecue sauce in a clear glass flask bottle.",
    shelf_location: "",
  },
  {
    name: "Saba Hot Sauce Small Bottle",
    category: "Oil & Condiments",
    description:
      "Saba brand chili hot sauce in a small narrow glass bottle with a red cap.",
    shelf_location: "",
  },
  {
    name: "Signal Cavity Fighter Toothpaste",
    category: "Personal Care",
    description:
      "Signal brand cavity fighter toothpaste in a blue and white box.",
    shelf_location: "",
  },
  {
    name: "Colgate Kids Toothpaste",
    category: "Personal Care",
    description:
      "Colgate brand toothpaste for kids featuring cartoon animal packaging.",
    shelf_location: "",
  },
  {
    name: "Dabur Herb'l Toothpaste Mint & Lemon",
    category: "Personal Care",
    description:
      "Dabur Herb'l Intense Fresh toothpaste, mint and lemon variant.",
    shelf_location: "",
  },
  {
    name: "Closeup Toothpaste Red Hot Triple Fresh",
    category: "Personal Care",
    description:
      "Closeup gel toothpaste Red Hot Triple Fresh formula in a bright red box.",
    shelf_location: "",
  },
  {
    name: "Closeup Toothpaste Menthol Fresh Triple Fresh",
    category: "Personal Care",
    description:
      "Closeup gel toothpaste Menthol Fresh Triple Fresh formula in a green box.",
    shelf_location: "",
  },
  {
    name: "Colgate Triple Action Toothpaste",
    category: "Personal Care",
    description: "Colgate brand Triple Action cavity protection toothpaste.",
    shelf_location: "",
  },
  {
    name: "Colgate Herbal Toothpaste",
    category: "Personal Care",
    description:
      "Colgate brand Herbal toothpaste featuring a green and red box design.",
    shelf_location: "",
  },
  {
    name: "Sensodyne Multi Care Toothpaste",
    category: "Personal Care",
    description: "Sensodyne brand daily sensitivity protection toothpaste.",
    shelf_location: "",
  },
  {
    name: "Yes Household Candles Pack",
    category: "Household & Cleaning",
    description: "Yes brand household illuminating candles in a dark blue box.",
    shelf_location: "",
  },
  {
    name: "Wooden Hand Scrub Brush",
    category: "Household & Cleaning",
    description:
      "Heavy-duty hand scrub brush with a curved wooden handle and colored bristles.",
    shelf_location: "",
  },
  {
    name: "Kiwi Shoe Polish Black Tin",
    category: "Household & Cleaning",
    description:
      "Classic Kiwi brand quality shoe paste polish in a round black metal tin.",
    shelf_location: "",
  },
  {
    name: "Plastic Dustpan Set with Handle",
    category: "Household & Cleaning",
    description: "Plastic hand dustpan and matching short broom set.",
    shelf_location: "",
  },
  {
    name: "Toilet Cleaning Brush with Base Holder",
    category: "Household & Cleaning",
    description:
      "Long-handled toilet cleaning brush resting in its matching plastic floor base.",
    shelf_location: "",
  },
  {
    name: "Plastic Laundry Brush",
    category: "Household & Cleaning",
    description:
      "Handheld plastic laundry and scrubbing brush with an iron-style handle grip.",
    shelf_location: "",
  },
  {
    name: "Patterned Toilet Brush with Lace Base Holder",
    category: "Household & Cleaning",
    description:
      "Long-handled toilet cleaning brush with a decorative patterned plastic cutout base.",
    shelf_location: "",
  },
  {
    name: "Heavy Duty Rubber Toilet Plunger",
    category: "Household & Cleaning",
    description:
      "Standard bathroom toilet plunger featuring a bright red rubber suction cup.",
    shelf_location: "",
  },
];

// ── Category name → id mapper ─────────────────────────────────
function matchCategory(categoryName, categories) {
  if (!categoryName) return null;
  const name = categoryName.trim().toLowerCase();
  // Exact match first
  const exact = categories.find((c) => c.name.toLowerCase() === name);
  if (exact) return exact.id;
  // Partial match
  const partial = categories.find(
    (c) =>
      c.name.toLowerCase().includes(name) ||
      name.includes(c.name.toLowerCase()),
  );
  return partial?.id ?? null;
}

// ── Duplicate detection ────────────────────────────────────────
// Priority: sku_code (exact) > barcode (exact) > name (case-insensitive
// exact). Returns which field matched, or null if none did.
function findDuplicateMatch(row, existingProducts) {
  if (row.sku_code) {
    const m = existingProducts.find((p) => p.sku_code && p.sku_code === row.sku_code);
    if (m) return "sku_code";
  }
  if (row.barcode) {
    const m = existingProducts.find((p) => p.barcode && p.barcode === row.barcode);
    if (m) return "barcode";
  }
  if (row.name) {
    const m = existingProducts.find(
      (p) => p.name && p.name.toLowerCase() === row.name.toLowerCase(),
    );
    if (m) return "name";
  }
  return null;
}

// ── Parse Excel/CSV file ──────────────────────────────────────
function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

// ── Normalize column names ────────────────────────────────────
function normalizeRow(row) {
  const get = (keys) => {
    for (const k of keys) {
      const found = Object.keys(row).find(
        (rk) => rk.trim().toLowerCase() === k.toLowerCase(),
      );
      if (found !== undefined) return String(row[found] ?? "").trim();
    }
    return "";
  };
  return {
    name: get(["product name", "name", "item", "product"]),
    category: get(["category", "category name"]),
    description: get(["description", "desc"]),
    selling_price: get(["selling price", "price", "sell price", "unit price"]),
    shelf_location: get(["shelf location", "shelf", "location"]),
    barcode: get(["barcode", "barcode number"]),
    sku_code: get(["sku", "sku code", "code"]),
    cost_price: get(["cost price", "cost", "purchase price"]),
  };
}

// ── Downloadable template ─────────────────────────────────────
// Headers match the first (primary) alias normalizeRow() recognizes for
// each field, so a file generated here round-trips through parsing with
// zero guessing on the user's part. Only "Product Name" is actually
// required — the rest are included so the accepted shape is obvious.
const TEMPLATE_HEADERS = [
  "Product Name",
  "Category",
  "Selling Price",
  "Cost Price",
  "Barcode",
  "SKU",
  "Shelf Location",
  "Description",
];

function downloadTemplate() {
  const exampleRows = [
    {
      "Product Name": "EXAMPLE — Coca-Cola 500ml (delete this row)",
      Category: "Beverages",
      "Selling Price": 25,
      "Cost Price": 18,
      Barcode: "6001234567890",
      SKU: "BEV-001",
      "Shelf Location": "Beverage Cooler",
      Description: "Classic Coca-Cola soft drink, 500ml plastic bottle",
    },
    {
      "Product Name": "EXAMPLE — Head & Shoulders Shampoo 400ml (delete this row)",
      Category: "Personal Care",
      "Selling Price": 180,
      "Cost Price": 140,
      Barcode: "",
      SKU: "",
      "Shelf Location": "Aisle 3",
      Description: "",
    },
  ];

  const ws = XLSX.utils.json_to_sheet(exampleRows, {
    header: TEMPLATE_HEADERS,
  });
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 24 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Products");
  XLSX.writeFile(wb, "suqpos-product-import-template.xlsx");
}

// ── Main BulkImport component ─────────────────────────────────
export default function BulkImport({ categories, storeId, onDone, onClose }) {
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);
  const [tab, setTab] = useState(
    DEFAULT_CATALOG_IMPORT_ENABLED ? "catalog" : "file",
  ); // "catalog" | "file"
  const [preview, setPreview] = useState([]); // rows to import
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // { imported, skipped, errors }
  const [fileError, setFileError] = useState(null);
  const [existingProducts, setExistingProducts] = useState([]);
  const [loadingExistingProducts, setLoadingExistingProducts] = useState(true);
  const fileRef = useRef(null);

  // ── Load existing products for this store, for duplicate detection ──
  // Independent fetch (same pattern as storeId) rather than a new prop
  // from Products.js — re-fires once storeId actually arrives.
  useEffect(() => {
    if (!storeId) return;
    supabase
      .from("products")
      .select("id, name, sku_code, barcode")
      .eq("store_id", storeId)
      .then(({ data, error }) => {
        logSupabaseError("BulkImport: fetch existingProducts", error);
        if (data) setExistingProducts(data);
        setLoadingExistingProducts(false);
      });
  }, [storeId]);

  // ── Load catalog ──────────────────────────────────────────
  function loadCatalog() {
    if (loadingExistingProducts) {
      alert("Still loading product data, try again in a moment.");
      return;
    }
    const rows = ETHIOPIAN_CATALOG.map((p, i) => {
      const category_id = matchCategory(p.category, categories);
      const duplicateMatchField = findDuplicateMatch(
        { name: p.name, barcode: "", sku_code: "" },
        existingProducts,
      );
      return {
        _idx: i,
        name: p.name,
        category: p.category,
        category_id,
        categoryWillBeCreated: !!p.category && !category_id,
        description: p.description,
        selling_price: "",
        shelf_location: p.shelf_location,
        barcode: "",
        sku_code: "",
        cost_price: "",
        isDuplicate: !!duplicateMatchField,
        duplicateMatchField,
      };
    });
    setPreview(rows);
    setSelected(new Set(rows.filter((r) => !r.isDuplicate).map((r) => r._idx)));
    setResult(null);
  }

  // ── Load file ─────────────────────────────────────────────
  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (loadingExistingProducts) {
      alert("Still loading product data, try again in a moment.");
      e.target.value = "";
      return;
    }
    setFileError(null);
    try {
      const rows = await parseFile(file);

      if (rows.length === 0) {
        setFileError(
          "This file appears to be empty — add at least one product row below the header, then upload again.",
        );
        return;
      }

      const normalized = rows
        .map((r, i) => {
          const n = normalizeRow(r);
          const category_id = matchCategory(n.category, categories);
          const duplicateMatchField = findDuplicateMatch(n, existingProducts);
          return {
            _idx: i,
            name: n.name,
            category: n.category,
            category_id,
            categoryWillBeCreated: !!n.category && !category_id,
            description: n.description,
            selling_price: n.selling_price,
            shelf_location: n.shelf_location,
            barcode: n.barcode,
            sku_code: n.sku_code,
            cost_price: n.cost_price,
            isDuplicate: !!duplicateMatchField,
            duplicateMatchField,
          };
        })
        .filter((r) => r.name);

      if (normalized.length === 0) {
        setFileError(
          "No products found — missing required column: Product Name (also accepted: Name, Item, Product). Check the downloadable template for the expected format.",
        );
        return;
      }

      setPreview(normalized);
      setSelected(new Set(normalized.filter((r) => !r.isDuplicate).map((r) => r._idx)));
      setResult(null);
    } catch (err) {
      setFileError("Could not read file: " + err.message);
    } finally {
      // Reset so re-selecting the same filename (e.g. after fixing it)
      // fires onChange again — browsers don't fire it for an unchanged value.
      e.target.value = "";
    }
  }

  // ── Toggle selection ──────────────────────────────────────
  function toggleSelect(idx) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === preview.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.map((r) => r._idx)));
    }
  }

  // ── Import ────────────────────────────────────────────────
  async function handleImport() {
    if (selected.size === 0) return;
    if (!storeId) {
      alert("Still loading your store info — please wait a moment and try again.");
      return;
    }
    setImporting(true);

    let toImport = preview.filter((r) => selected.has(r._idx));

    // ── Auto-create categories referenced by this batch but not yet matched ──
    const missingNames = new Map(); // lowercase name -> original casing (first seen)
    toImport.forEach((row) => {
      if (row.category && !row.category_id) {
        const key = row.category.trim().toLowerCase();
        if (!missingNames.has(key)) missingNames.set(key, row.category.trim());
      }
    });
    // Re-check against the live categories prop in case it changed since
    // the preview was built (dedupe within this batch happens via the Map key).
    categories.forEach((c) => missingNames.delete(c.name.trim().toLowerCase()));

    const nameToId = {};
    categories.forEach((c) => {
      nameToId[c.name.trim().toLowerCase()] = c.id;
    });

    if (missingNames.size > 0) {
      const { data: newCategories, error: catError } = await supabase
        .from("categories")
        .insert(
          Array.from(missingNames.values()).map((name) => ({
            name,
            store_id: storeId,
          })),
        )
        .select("id, name");
      if (catError) {
        alert("❌ Failed to create new categories for this import: " + catError.message);
        setImporting(false);
        return;
      }
      (newCategories ?? []).forEach((c) => {
        nameToId[c.name.trim().toLowerCase()] = c.id;
      });
    }

    toImport = toImport.map((row) =>
      row.category && !row.category_id
        ? {
            ...row,
            category_id: nameToId[row.category.trim().toLowerCase()] ?? row.category_id,
          }
        : row,
    );

    let imported = 0;
    let skipped = 0;
    const errors = [];

    for (const row of toImport) {
      if (!row.name) {
        skipped++;
        continue;
      }
      const payload = {
        name: row.name,
        description: row.description || null,
        selling_price: Number(row.selling_price) || 0,
        cost_price: Number(row.cost_price) || 0,
        category_id: row.category_id || null,
        shelf_location: row.shelf_location || null,
        barcode: row.barcode || null,
        sku_code: row.sku_code || null,
        is_active: true,
        stock_qty: 0,
        low_stock_threshold: 5,
        purchase_unit: "piece",
        sale_unit: "piece",
        units_per_purchase: 1,
        store_id: storeId,
      };

      const { error } = await supabase.from("products").insert(payload);
      if (error) {
        if (
          error.message.includes("duplicate") ||
          error.message.includes("unique")
        ) {
          skipped++;
        } else {
          errors.push({ name: row.name, error: error.message });
        }
      } else {
        imported++;
      }
    }

    setImporting(false);
    setResult({ imported, skipped, errors });
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "900px" }}>
        {/* Header */}
        <div className="report-header">
          <div>
            <h2 className="report-title">📥 Bulk Import Products</h2>
            <p className="report-date">
              {DEFAULT_CATALOG_IMPORT_ENABLED
                ? "Import from your Ethiopian product catalog or upload your own file"
                : "Upload a spreadsheet of your products to get started"}
            </p>
          </div>
          <button
            className="maximize-btn"
            onClick={toggle}
            title={icon === "⊞" ? "Maximize" : "Restore"}
          >
            {icon}
          </button>
          <button className="report-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Tabs — only shown when there's an actual choice to make */}
        {DEFAULT_CATALOG_IMPORT_ENABLED && (
          <div style={{ padding: "0 20px" }}>
            <div className="inv-tabs">
              <button
                className={`inv-tab ${tab === "catalog" ? "active" : ""}`}
                onClick={() => {
                  setTab("catalog");
                  setPreview([]);
                  setResult(null);
                }}
              >
                🇪🇹 Ethiopian Catalog
              </button>
              <button
                className={`inv-tab ${tab === "file" ? "active" : ""}`}
                onClick={() => {
                  setTab("file");
                  setPreview([]);
                  setResult(null);
                }}
              >
                📂 Upload File
              </button>
            </div>
          </div>
        )}

        <div className="report-table-wrap" style={{ padding: "16px 20px" }}>
          {/* Result banner */}
          {result && (
            <div
              style={{
                padding: "12px 16px",
                background:
                  result.errors.length > 0
                    ? "var(--amber-dim)"
                    : "var(--green-dim)",
                border: `1px solid ${result.errors.length > 0 ? "rgba(255,179,64,0.3)" : "rgba(50,215,75,0.3)"}`,
                borderRadius: "var(--radius)",
                marginBottom: "16px",
                fontSize: "13px",
              }}
            >
              <strong>
                ✅ {result.imported} imported
                {result.skipped > 0
                  ? ` · ⏭ ${result.skipped} skipped (already exist)`
                  : ""}
                {result.errors.length > 0
                  ? ` · ❌ ${result.errors.length} failed`
                  : ""}
              </strong>
              {result.errors.length > 0 && (
                <ul
                  style={{
                    marginTop: "8px",
                    paddingLeft: "16px",
                    color: "var(--red)",
                  }}
                >
                  {result.errors.slice(0, 5).map((e, i) => (
                    <li key={i} style={{ fontSize: "12px" }}>
                      {e.name}: {e.error}
                    </li>
                  ))}
                </ul>
              )}
              {result.imported > 0 && (
                <button
                  onClick={onDone}
                  style={{
                    marginTop: "10px",
                    padding: "6px 16px",
                    background: "var(--green)",
                    color: "#000",
                    border: "none",
                    borderRadius: "var(--radius)",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Done — View Products
                </button>
              )}
            </div>
          )}

          {/* Catalog tab */}
          {DEFAULT_CATALOG_IMPORT_ENABLED &&
            tab === "catalog" &&
            preview.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>🇪🇹</div>
              <h3 style={{ color: "var(--text)", marginBottom: "8px" }}>
                Ethiopian Mini Market Catalog
              </h3>
              <p
                style={{
                  color: "var(--text3)",
                  fontSize: "13px",
                  marginBottom: "24px",
                  maxWidth: "400px",
                  margin: "0 auto 24px",
                }}
              >
                {ETHIOPIAN_CATALOG.length} common products found in Ethiopian
                mini markets — beverages, snacks, personal care, household
                items, grains, and more. Select the ones you carry and set
                prices after importing.
              </p>
              <button className="save-btn" onClick={loadCatalog}>
                📋 Load Catalog
              </button>
            </div>
          )}

          {/* File tab */}
          {tab === "file" && preview.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📂</div>
              <h3 style={{ color: "var(--text)", marginBottom: "8px" }}>
                Upload Product File
              </h3>
              <p
                style={{
                  color: "var(--text3)",
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                Supports Excel (.xlsx) and CSV files. Required column:{" "}
                <strong>Product Name</strong>. Optional: Category, Description,
                Selling Price, Cost Price, Shelf Location, Barcode, SKU.
              </p>
              <button
                onClick={downloadTemplate}
                style={{
                  background: "none",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--cyan)",
                  fontSize: "12px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  marginBottom: "16px",
                }}
              >
                📄 Download template (.xlsx)
              </button>
              {fileError && (
                <p
                  style={{
                    color: "var(--red)",
                    fontSize: "12px",
                    marginBottom: "12px",
                  }}
                >
                  ⚠ {fileError}
                </p>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                style={{ display: "none" }}
              />
              <button
                className="save-btn"
                onClick={() => fileRef.current?.click()}
              >
                📂 Choose File
              </button>
              <p
                style={{
                  fontSize: "11px",
                  color: "var(--text3)",
                  marginTop: "12px",
                }}
              >
                Your file columns will be matched automatically
              </p>
            </div>
          )}

          {/* Preview table */}
          {preview.length > 0 && !result && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      color: "var(--text2)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.size === preview.length}
                      onChange={toggleAll}
                      style={{ accentColor: "var(--cyan)" }}
                    />
                    Select all ({preview.length})
                  </label>
                  <span style={{ fontSize: "12px", color: "var(--text3)" }}>
                    {selected.size} selected
                  </span>
                </div>
                <button
                  className="save-btn"
                  onClick={handleImport}
                  disabled={importing || selected.size === 0}
                  style={{ minWidth: "140px" }}
                >
                  {importing
                    ? `Importing... (${selected.size})`
                    : `📥 Import ${selected.size} Products`}
                </button>
              </div>

              <div
                style={{
                  fontSize: "11px",
                  color: "var(--amber)",
                  marginBottom: "10px",
                }}
              >
                💡 Products without prices will be imported with ETB 0 — update
                prices after importing.
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: "32px" }}></th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Price (ETB)</th>
                    <th>Shelf</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row) => (
                    <tr
                      key={row._idx}
                      style={{ opacity: selected.has(row._idx) ? 1 : 0.4 }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(row._idx)}
                          onChange={() => toggleSelect(row._idx)}
                          style={{ accentColor: "var(--cyan)" }}
                        />
                      </td>
                      <td style={{ fontSize: "12px", fontWeight: 500 }}>
                        <span style={{ color: row.isDuplicate ? "var(--red)" : "inherit" }}>
                          {row.name}
                        </span>
                        {row.isDuplicate && (
                          <div
                            style={{ fontSize: "10px", color: "var(--red)", marginTop: "2px" }}
                            title={`Matches an existing product by ${row.duplicateMatchField}`}
                          >
                            ⚠ Already exists ({row.duplicateMatchField})
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: "11px",
                            color: row.category_id
                              ? "var(--cyan)"
                              : "var(--amber)",
                          }}
                          title={
                            row.categoryWillBeCreated
                              ? "This category will be created on import"
                              : undefined
                          }
                        >
                          {row.category || "—"}
                          {row.categoryWillBeCreated && " ⚠"}
                        </span>
                      </td>
                      <td
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "12px",
                          color: row.selling_price
                            ? "var(--text)"
                            : "var(--text3)",
                        }}
                      >
                        {row.selling_price
                          ? `ETB ${Number(row.selling_price).toFixed(2)}`
                          : "Set later"}
                      </td>
                      <td style={{ fontSize: "11px", color: "var(--text3)" }}>
                        {row.shelf_location || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preview.some((r) => r.categoryWillBeCreated) && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "var(--amber)",
                    marginTop: "10px",
                  }}
                >
                  ⚠ Categories marked with ⚠ don't exist yet — they'll be
                  created automatically when you import.
                </p>
              )}

              {preview.some((r) => r.isDuplicate) && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "var(--red)",
                    marginTop: "6px",
                  }}
                >
                  ⚠ {preview.filter((r) => r.isDuplicate).length} product(s)
                  appear to already exist and were left unchecked — check the
                  box to import anyway.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
